import {useCallback, useEffect, useRef, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {invoiceService} from '@/services/invoiceService';
import type {AdminBusinessInvoice, BusinessInvoiceListParams} from '@/types';
import {logRejection} from '@/utils/logRejection';

import type {InvoiceListProps} from './types';

const PAGE_SIZE = 10;

/**
 * Merchant fee-invoice list.
 *
 * Deliberately paginates SERVER-SIDE, unlike the sibling `useFeeList`: that
 * hook fetches every fee and slices in memory because the fees endpoint has no
 * "organization defaults only" filter, which is a workaround for that endpoint
 * rather than a pattern to copy. The filter and pagination surface is kept
 * identical to `useFeeList` so `Pagination` and the filter components drop in
 * unchanged.
 */
const useInvoiceList = (
  /**
   * Scopes every request to one merchant. Set when the list is rendered inside
   * a merchant's own page; the global tab passes nothing.
   */
  scope: {
    businessId?: string;
    /**
     * Waits for a required dependency (the merchant's id) before fetching, so
     * the merchant-scoped tab never requests, and momentarily renders, the
     * unscoped list while the business is still loading.
     */
    enabled?: boolean;
  } = {},
): InvoiceListProps => {
  const {enabled = true} = scope;
  const [list, setList] = useState<AdminBusinessInvoice[]>([]);
  // Starts loading whether or not the fetch may run yet, so the tab shows a
  // skeleton rather than "no invoices" until the first response lands.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  // Last-fetched page (0-indexed, matching the request params) so creating an
  // invoice refetches in place instead of dropping the admin back to page one.
  const currentPageRef = useRef<number>(0);
  // Identifies the newest request, so a slower response from an older page can
  // never overwrite the page the admin has already moved to. `Pagination`
  // requests page one again when it mounts under a non-empty list, so
  // overlapping requests are the normal case here, not an edge one.
  const latestRequestRef = useRef<number>(0);
  // 1-indexed page synced back into Pagination after a post-action refetch.
  const [displayPage, setDisplayPage] = useState<number>(1);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] =
    useState<BusinessInvoiceListParams>(
      scope.businessId ? {businessId: scope.businessId} : {},
    );
  const [filtersToApply, setFiltersToApply] =
    useState<BusinessInvoiceListParams>({});
  const [filterCount, setFilterCount] = useState<number>(0);

  // useFilters validates the date pair (both required, from before to) and
  // normalises them to ISO day bounds before they reach the request.
  const {applyFilters} = useFilters(setAppliedFilters);

  const fetchInvoices = useCallback(
    async (params: BusinessInvoiceListParams) => {
      // Defer until the scope is ready (the merchant is known), so this tab
      // never fetches the unscoped list first.
      if (!enabled) {
        return;
      }
      const requestId = ++latestRequestRef.current;
      try {
        setError(null);
        setIsLoading(true);
        const requestedPage = params.page ?? 0;
        const response = await invoiceService.getInvoices({
          ...appliedFilters,
          // A merchant-scoped list cannot be filtered off its own merchant.
          ...(scope.businessId ? {businessId: scope.businessId} : {}),
          page: requestedPage,
          limit: PAGE_SIZE,
        });
        // A newer page was requested while this one was in flight: that one
        // owns the list now, so drop this response instead of reverting.
        if (requestId !== latestRequestRef.current) {
          return;
        }
        currentPageRef.current = requestedPage;
        setDisplayPage(requestedPage + 1);
        setPage(requestedPage + 1);
        setList(response.data ?? []);
        setItemTotal(response.total ?? 0);
      } catch (e) {
        if (requestId === latestRequestRef.current) {
          setError(e);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [appliedFilters, scope.businessId, enabled],
  );

  // Pagination's onPageChange is `=> void`, so adapt at the boundary rather
  // than widening either contract. fetchInvoices catches into `error` state, so
  // this only fires if that catch or the finally itself throws.
  const fetchList = useCallback(
    (params: BusinessInvoiceListParams) => {
      fetchInvoices(params).catch(logRejection('Failed to load invoice list'));
    },
    [fetchInvoices],
  );

  const refetchCurrentPage = useCallback(async () => {
    await fetchInvoices({page: currentPageRef.current});
  }, [fetchInvoices]);

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updated = {...prev, [name]: value};
        setFilterCount(
          Object.values(updated).filter(
            (val) => val !== undefined && val !== '',
          ).length,
        );
        return updated;
      });
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    applyFilters(filtersToApply);
    // Bounce pagination back to page one: the filtered result set is a
    // different list, so holding page 5 would show an empty page.
    setIsPageReset((prev) => !prev);
  }, [applyFilters, filtersToApply]);

  const clearFilters = useCallback(() => {
    setAppliedFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    error,
    isLoading,
    list,
    pageInfo: {total: itemTotal, page},
    fetchList,
    refetchCurrentPage,
    displayPage,
    isPageReset,
    filterCount,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
  };
};

export default useInvoiceList;
